/**
 * Deterministic, seeded pseudo-random number generators.
 *
 * The entire point of keeping `core/` pure is reproducibility: given the same
 * seed and the same player inputs, the simulation produces byte-identical results
 * in the browser (Phaser) AND on the server (a Cloud Function re-running the run).
 * That one property is what powers anti-cheat (server re-simulation of a
 * submitted run) and POPSTAR LIVE replays.
 *
 * RULE: never call `Math.random()` anywhere inside the simulation — draw from one
 * of these instead. (Seed *generation* for a new run happens OUTSIDE core, in the
 * game layer, and is passed in as the run's input.)
 */
import type { Rng } from './types';

/**
 * mulberry32 — a tiny, fast 32-bit PRNG with solid statistical quality for game
 * use. The state update is pure integer math (via {@link Math.imul}) so it's
 * deterministic across JS engines. Returns a float in [0, 1).
 */
export function createRng(seed: number): Rng {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Derive an independent stream seed from a base seed + a label. Lets separate
 * concerns (e.g. attack selection vs. a pattern's internal jitter) draw from
 * distinct streams, so adding a draw in one place doesn't shift the others —
 * which keeps unrelated runs reproducible as the game evolves. FNV-1a based.
 */
export function deriveSeed(base: number, label: string): number {
  let h = base >>> 0;
  for (let i = 0; i < label.length; i++) {
    h = Math.imul(h ^ label.charCodeAt(i), 0x01000193) >>> 0;
  }
  return h >>> 0;
}
